import type React from 'react';
import { useSelector } from 'react-redux';

import * as styles from './styles';
import { Button } from '@cobliteam/ui/components';
import { cn } from '@cobliteam/ui/utils';
import moment from 'moment';
import queryString from 'query-string';

import { type Geofence, GeofenceCategory, Location, type TimestampInMillis, type UUID } from '@cobliteam/common-types';

import { isWebDataSuccess } from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import { selectAddress } from '@cobliteam/store';

import { usePermissions } from '@cobliteam/hooks';

import { ContainerDefault, ContentLoader, DescriptionListDefault, DescriptionListMedium, DescriptionTitle } from '@cobliteam/base-components';

import Icon from '@cobliteam/ui/icons';

import TrackingPermissions from '../../../../permissions';

export interface StartEndPopupProps {
  timestamp: TimestampInMillis;
  type: 'start' | 'end';
  vehicleId: UUID;
  geofences: Geofence[];
  latitude?: number;
  longitude?: number;
  idleWithEngineOnInMillis: number;
  idleTimeHighlighted?: boolean;
  driverName?: string;
}

const addressPlaceholder = () => (
  <ContentLoader width={250} height={14} className={styles.addressPlaceholder}>
    <rect x='0' y='0' width='100%' height='100%' />
  </ContentLoader>
);

export const StartEndPopup: React.FC<StartEndPopupProps> = ({
  timestamp,
  type,
  vehicleId,
  geofences,
  latitude,
  longitude,
  idleWithEngineOnInMillis,
  idleTimeHighlighted,
  driverName,
}) => {
  const { isAccessAllowed } = usePermissions();
  const isAllowedViewGeofences = isAccessAllowed(TrackingPermissions.VIEW_PLACES_OF_INTEREST);

  const addressWebData = useSelector(selectAddress);
  const address = isWebDataSuccess(addressWebData) ? addressWebData.data : addressPlaceholder();

  const formatDuration = (valueInMillis: number): string => {
    const duration = moment.duration(valueInMillis);
    const hours = duration.days() * 24 + duration.hours();
    const hoursStr = hours ? `${hours} h ` : '';
    const minutesStr = duration.minutes() ? `${duration.minutes()} min ` : '';
    const secondsStr = duration.seconds() ? `${duration.seconds()} s` : '';
    return `${hoursStr}${minutesStr}${secondsStr}`;
  };

  const formatHour = (valueInMillis: number): string => moment(valueInMillis, 'x').format('HH:mm');

  const renderBadge = (style: string, label: string, icon: JSX.Element) => (
    <div className={cn(styles.badge, style)}>
      {icon}
      {label}
    </div>
  );

  const renderGeofenceBadge = (category?: GeofenceCategory) => {
    switch (category) {
      case GeofenceCategory.BASE:
        return renderBadge(styles.badgeBase, 'Base', <Icon name='building' />);
      case GeofenceCategory.CLIENT:
        return renderBadge(styles.badgeClient, 'Cliente', <Icon name='user-round' />);
      case GeofenceCategory.OTHER:
        return renderBadge(styles.badgeOther, 'Outro', <Icon name='map-pin' />);
      default:
        return renderBadge(styles.badgeDefault, 'Tipo não cadastrado', <Icon name='map-pin' />);
    }
  };

  const getTitle = () => `${type === 'start' ? 'Início' : 'Fim'} do trajeto | ${formatHour(timestamp)}`;

  const renderInfoRow = (id: string, label: string, value: string | JSX.Element) => (
    <DescriptionListDefault id={id}>
      <DescriptionListMedium>
        {label}
        :&nbsp;
      </DescriptionListMedium>
      {value}
    </DescriptionListDefault>
  );

  const handleGoToTrackingTab = () => {
    Analytics.track('Opened Vehicle Tracking On Map From Tracking History Start End Popup', { source: type });

    const params = queryString.stringify({
      vehicle_id: vehicleId,
      date: timestamp,
    });
    const url = `${window.location.origin}#${Location.TRACKING}?${params}`;
    window.open(url, '_blank')!.focus();
  };

  return (
    <ContainerDefault className={styles.popup}>
      <div className={styles.titleWrapper}>
        <div className={styles.header}>
          <DescriptionTitle>{getTitle()}</DescriptionTitle>
          {isAllowedViewGeofences && geofences.length > 0 && renderGeofenceBadge(geofences[0].category)}
        </div>
        {isAllowedViewGeofences && geofences.length > 0 && <DescriptionTitle className={styles.subTitle}>{geofences[0].name}</DescriptionTitle>}
      </div>
      <div className={styles.body}>
        {!!latitude && !!longitude && renderInfoRow('address', 'Endereço', address)}
        {renderInfoRow('driver', 'Motorista', driverName || 'Sem motorista')}

        {idleTimeHighlighted && (
          <div className={styles.idleHighlight}>
            <Icon name='triangle-alert' />
            {renderInfoRow('idle', 'Motor ocioso', formatDuration(idleWithEngineOnInMillis))}
          </div>
        )}
      </div>

      <div className={styles.trackingActionArea}>
        <Button variant='text' startIcon={<Icon name='route' />} onClick={handleGoToTrackingTab}>
          Ver o trajeto
        </Button>
      </div>
    </ContainerDefault>
  );
};
